import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../apiServices/api_routes';
import { hardRefresh } from '../utils/hardRefresh';

const POLL_INTERVAL_MS = 3 * 60 * 1000;
const SNOOZE_MS = 30 * 60 * 1000;
const SNOOZE_STORAGE_KEY = 'lens_update_snooze_until';

const UPDATE_CHECK_ENABLED = process.env.REACT_APP_ENABLE_UPDATE_CHECK === 'true';
const CLIENT_FRONTEND_VERSION = process.env.REACT_APP_BUILD_ID || '';

const joinUrl = (base, path) => {
    const normalizedBase = (base || '').replace(/\/+$/, '');
    const normalizedPath = path.replace(/^\/+/, '');
    return `${normalizedBase}/${normalizedPath}`;
};

const fetchJsonNoStore = async url => {
    const response = await fetch(url, {
        cache: 'no-store',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
        throw new Error(`version fetch failed: ${response.status}`);
    }
    return response.json();
};

const readSnoozeUntil = () => {
    try {
        const raw = sessionStorage.getItem(SNOOZE_STORAGE_KEY);
        if (!raw) return 0;
        const value = Number(raw);
        return Number.isFinite(value) ? value : 0;
    } catch (_) {
        return 0;
    }
};

const writeSnoozeUntil = until => {
    try {
        sessionStorage.setItem(SNOOZE_STORAGE_KEY, String(until));
    } catch (_) {
        // ignore
    }
};

/**
 * Polls frontend /version.json and backend /version/ for deploy fingerprints.
 * Enabled only when REACT_APP_ENABLE_UPDATE_CHECK=true (prod/staging CI).
 */
const useAppUpdateCheck = () => {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showBanner, setShowBanner] = useState(false);

    const clientBackendRef = useRef(null);
    const inFlightRef = useRef(false);

    const applyUpdateAvailable = useCallback(() => {
        setUpdateAvailable(true);
        setShowBanner(true);
        const snoozeUntil = readSnoozeUntil();
        if (Date.now() >= snoozeUntil) {
            setShowModal(true);
        }
    }, []);

    const checkVersions = useCallback(async () => {
        if (!UPDATE_CHECK_ENABLED || inFlightRef.current) return;
        inFlightRef.current = true;

        try {
            let frontendChanged = false;
            let backendChanged = false;

            if (CLIENT_FRONTEND_VERSION) {
                try {
                    const data = await fetchJsonNoStore(`/version.json?t=${Date.now()}`);
                    const remoteFrontend = (data?.version || '').trim();
                    if (remoteFrontend && remoteFrontend !== CLIENT_FRONTEND_VERSION) {
                        frontendChanged = true;
                    }
                } catch (_) {
                    // ignore transient frontend version failures
                }
            }

            if (API_BASE_URL) {
                try {
                    const data = await fetchJsonNoStore(joinUrl(API_BASE_URL, `version/?t=${Date.now()}`));
                    const remoteBackend = (data?.version || '').trim();
                    if (remoteBackend && remoteBackend !== 'unknown') {
                        if (clientBackendRef.current == null) {
                            clientBackendRef.current = remoteBackend;
                        } else if (remoteBackend !== clientBackendRef.current) {
                            backendChanged = true;
                        }
                    }
                } catch (_) {
                    // ignore transient backend version failures
                }
            }

            if (frontendChanged || backendChanged) {
                applyUpdateAvailable();
            }
        } finally {
            inFlightRef.current = false;
        }
    }, [applyUpdateAvailable]);

    useEffect(() => {
        if (!UPDATE_CHECK_ENABLED) return undefined;

        checkVersions();
        const intervalId = window.setInterval(checkVersions, POLL_INTERVAL_MS);

        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                checkVersions();
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', checkVersions);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', checkVersions);
        };
    }, [checkVersions]);

    const dismissModal = useCallback(() => {
        writeSnoozeUntil(Date.now() + SNOOZE_MS);
        setShowModal(false);
        setShowBanner(true);
    }, []);

    const openModal = useCallback(() => {
        setShowModal(true);
    }, []);

    const refreshNow = useCallback(() => {
        hardRefresh();
    }, []);

    return {
        updateAvailable,
        showModal,
        showBanner,
        dismissModal,
        openModal,
        refreshNow,
    };
};

export default useAppUpdateCheck;
